import {
  WSAlert,
  WSMenuItem,
  WSPanel,
  WSSidebar,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useGetPayerEngagementQuery } from "../../../../query/payerEngagements/queries/useGetPayerEngagementQuery";
import { WSQueries } from "../../../../query/WSQuery";
import { IPayerEngagementResponse } from "@wingspanhq/payments/dist/interfaces/api/payerPayeeEngagement";
import { getParentPath } from "../../../../utils/goToParentRoute";
import { EngagementInfoPanel } from "./EngagementInfoPanel";
import React, { useMemo, useState } from "react";
import {
  RequirementStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces/eligibilityRequirement";
import { getEngagementEligibilityRequirementsCompleted } from "../../utils/engagement";
import {
  useDownloadMemberDocument,
  useSignDocument
} from "../../../../query/files/mutations";
import {
  IMemberClientRequirementResponse,
  IPayerResponse,
  PayerPayeeEngagementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { useGetClientQuery } from "../../../../query/clients/queries/useGetClientQuery";
import { selectorPayerEngagementName } from "../../../Payees/selectors/selectorPayerEngagements";

export const requirementTypeToLabelMap: Record<RequirementType, string> = {
  [RequirementType.Signature]: "e-Signature"
};

export const RouteEngagementDetails: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<{ clientId: string; engagementId: string }>();
  const { clientId, engagementId } = match.params;
  const queryEngagement = useGetPayerEngagementQuery(clientId, engagementId);
  const queryPayer = useGetClientQuery(clientId);

  const backPath = location.state?.backPath || getParentPath(history);

  return (
    <WSSidebar.Container
      onClose={() => {
        history.replace(backPath);
      }}
    >
      <WSQueries queries={{ queryEngagement, queryPayer }}>
        {({ queryEngagementData: payerEngagement, queryPayerData }) => {
          return (
            <WSSidebar.Layout
              header={selectorPayerEngagementName(payerEngagement, true)}
            >
              <Inner payerEngagement={payerEngagement} payer={queryPayerData} />
            </WSSidebar.Layout>
          );
        }}
      </WSQueries>
    </WSSidebar.Container>
  );
};

const Inner: React.FC<{
  payerEngagement: IPayerEngagementResponse;
  payer: IPayerResponse;
}> = ({ payerEngagement, payer }) => {
  const [showWarning, setShowWarning] = useState<boolean>(true);
  const [sign, signMeta] = useSignDocument();
  const [downloadPdf] = useDownloadMemberDocument();

  const tableData = useMemo(() => {
    const eligibilityRequirements =
      payerEngagement.eligibilityRequirements ?? [];
    return eligibilityRequirements.map(requirement => ({
      id: requirement.documentId as string,
      data: {
        requirement
      }
    }));
  }, [payerEngagement]);

  const columns: Array<WSTableColumn> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => (
        <WSTableCell text={rowItem.data.requirement.document.title || "--"} />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => (
        <WSTableCell
          icon="edit-line"
          text={
            requirementTypeToLabelMap[
              rowItem.data.requirement.requirementType as RequirementType
            ] ?? rowItem.data.requirement.requirementType
          }
        />
      )
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        const { requirement } = rowItem.data;
        {
          /*
          const statusToComponentMap: Record<RequirementStatus, WSPillProps> = {
           [RequirementStatus.Complete]: {
             icon: "check-circle",
             text: "Complete",
             theme: "success"
           },
           [RequirementStatus.Pending]: {
             icon: "alert",
             text: "Pending",
             theme: "warning"
           },
           [RequirementStatus.Sent]: {
             icon: "send-mail",
             text: "Sent",
             theme: "blue"
           },
           [RequirementStatus.New]: {
             icon: "envelope-open",
             text: "New",
             theme: "neutral"
           }
         };
         */
        }
        return (
          <WSTableCell
            // pill={statusToComponentMap[requirement.status as RequirementStatus]}
            pill={
              requirement.status === RequirementStatus.Complete
                ? {
                    theme: "success",
                    text: "Complete",
                    icon: true
                  }
                : {
                    theme: "warning",
                    text: "Pending",
                    icon: true
                  }
            }
            secondaryText={
              requirement.status === RequirementStatus.Pending
                ? requirement.document?.events?.memberSignedAt &&
                  !requirement.document?.events?.clientSignedAt
                  ? "Awaiting client signature"
                  : requirement.document?.events?.clientSignedAt &&
                    !requirement.document?.events?.memberSignedAt
                  ? "Your signature required"
                  : ""
                : ""
            }
          />
        );
      }
    }
  ];

  const handleRequirementDownload = (
    requirement: IMemberClientRequirementResponse
  ) => {
    downloadPdf({
      documentId: requirement?.documentId || ""
    });
  };

  const handleRequirementSign = async (
    requirement: IMemberClientRequirementResponse
  ) => {
    await sign({ documentId: requirement.documentId });
  };

  const areRequirementsCompleted = getEngagementEligibilityRequirementsCompleted(
    payerEngagement
  );
  const isArchived =
    payerEngagement.status === PayerPayeeEngagementStatus.Inactive;

  return (
    <>
      <EngagementInfoPanel engagement={payerEngagement} mb="XL" />
      {showWarning && !areRequirementsCompleted && !isArchived ? (
        <WSAlert
          theme="warning"
          icon="alert"
          title="Eligibility requirements incomplete"
          mb="L"
          onDismiss={() => setShowWarning(false)}
        >
          Complete all eligibility requirements to get paid.
        </WSAlert>
      ) : null}
      {tableData.length > 0 ? (
        <WSPanel noBorder p="NONE" mb="XL">
          <WSTable
            rowMenuActions={({ data: { requirement } }) => {
              const actions: WSMenuItem[] = [];

              if (requirement?.documentId) {
                actions.push({
                  onClick: () => {
                    handleRequirementDownload(requirement);
                  },
                  label: "Download PDF",
                  icon: "download"
                });
              }

              if (
                requirement.status !== RequirementStatus.Complete &&
                !(
                  requirement.status === RequirementStatus.Pending &&
                  requirement.document?.events?.memberSignedAt
                )
              ) {
                actions.push({
                  onClick: () => {
                    handleRequirementSign(requirement);
                  },
                  label: "Sign",
                  icon: "edit-line"
                });
              }

              return payerEngagement.status ===
                PayerPayeeEngagementStatus.Inactive
                ? []
                : actions;
            }}
            columns={columns}
            tableData={tableData}
            data-testid="engagementRequirementsTable"
          />
        </WSPanel>
      ) : null}
      <PayerIntegrationWidget
        isInactive={
          payerEngagement.status === PayerPayeeEngagementStatus.Inactive
        }
        payerId={payer.payerId}
        engagementId={payerEngagement.payerPayeeEngagementId}
        customerId={
          payerEngagement?.payeeOwnedData?.integration?.quickbooks?.customerId
        }
        itemId={
          payerEngagement?.payeeOwnedData?.integration?.quickbooks?.itemId
        }
      />
    </>
  );
};
